import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';

const PrivacyPolicy = () => {
  const navigate = useNavigate();
  const { darkMode } = useTheme();

  useEffect(() => {
    const badge = document.getElementById('emergent-badge');
    if (badge) badge.style.display = 'none';
    return () => {
      if (badge) badge.style.display = 'flex';
    };
  }, []);

  return (
    <div className={`min-h-screen ${darkMode ? 'bg-gray-900' : 'bg-white'}`}>
      <div className="pt-[env(safe-area-inset-top,12px)]" />
      
      <header className={`sticky top-0 border-b z-50 ${darkMode ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200'}`}>
        <div className="px-4 py-4 flex items-center gap-3">
          <button
            onClick={() => navigate('/settings')}
            className={`p-2 rounded-full transition-colors ${darkMode ? 'hover:bg-gray-800' : 'hover:bg-gray-100'}`}
          >
            <ArrowLeft className={`w-6 h-6 ${darkMode ? 'text-white' : 'text-gray-700'}`} />
          </button>
          <div>
            <h1 className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Privacy Policy</h1>
          </div>
        </div>
      </header>

      <div className="px-6 py-6">
        <div className={`prose max-w-none ${darkMode ? 'prose-invert' : ''}`}>
          <h2 className={`text-2xl font-bold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>SmartReadGo Privacy Policy</h2>
          <p className={`mb-4 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}><strong>Effective Date:</strong> 03 January 2026</p>
          
          <p className={`mb-6 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            This Privacy Policy explains how SmartReadGo (Proprietorship) ("SmartReadGo", "we", "our", "us"), based in Mumbai, Maharashtra, India, collects, uses, shares, and protects information when you use the SmartReadGo mobile application and related services (the "Service"). If you do not agree with this Policy, please do not use the Service.
          </p>

          <h3 className={`text-xl font-bold mt-6 mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>1) Who we are & how to contact us</h3>
          <p className={`mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>SmartReadGo (Proprietorship)</p>
          <p className={`mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Mumbai, Maharashtra, India</p>
          <p className={`mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Email: Admin@smartreadgo.com</p>
          <p className={`mb-4 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>WhatsApp: +91 9004685736</p>

          <h3 className={`text-xl font-bold mt-6 mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>2) What information we collect</h3>
          <p className={`mb-4 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>We collect information in the following categories:</p>
          
          <h4 className={`text-lg font-semibold mt-4 mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>A) Information you provide (optional)</h4>
          <ul className={`list-disc pl-6 mb-4 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            <li className="mb-2">Account information (if you choose to sign in): such as phone number, email, or other identifiers required for login</li>
            <li className="mb-2">Support communications: if you email or message us, we collect the contents of that communication and your contact details</li>
            <li className="mb-2">Note: SmartReadGo also supports guest access, which can be used without creating an account</li>
          </ul>

          <h4 className={`text-lg font-semibold mt-4 mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>B) Information collected automatically</h4>
          <p className={`mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>When you use the Service, we may automatically collect:</p>
          <ul className={`list-disc pl-6 mb-4 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            <li className="mb-2">Device information: device model, OS version, language, app version</li>
            <li className="mb-2">Usage data: screens viewed, features used (e.g., Home Mode/Drive Mode, play/stop), crash logs, performance metrics</li>
            <li className="mb-2">Approximate location (if enabled and you grant permission)</li>
          </ul>

          <h4 className={`text-lg font-semibold mt-4 mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>C) Advertising identifiers</h4>
          <p className={`mb-4 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>If you use the free/ad-supported version, we (or ad partners) may collect advertising ID and ad interaction events.</p>

          <h3 className={`text-xl font-bold mt-6 mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>3) How we use information</h3>
          <ul className={`list-disc pl-6 mb-4 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            <li className="mb-2">Provide and operate the Service</li>
            <li className="mb-2">Improve performance, reliability, and user experience</li>
            <li className="mb-2">Provide customer support</li>
            <li className="mb-2">Show ads and measure ad performance</li>
            <li className="mb-2">Maintain safety and prevent fraud</li>
            <li className="mb-2">Comply with legal obligations</li>
          </ul>

          <h3 className={`text-xl font-bold mt-6 mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>4) How we share information</h3>
          <p className={`mb-4 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>We do not sell your personal information. We may share information with service providers, for legal/safety reasons, or in business transfers.</p>

          <h3 className={`text-xl font-bold mt-6 mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>5) Your choices & controls</h3>
          <ul className={`list-disc pl-6 mb-4 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            <li className="mb-2">Use guest mode to reduce data collection</li>
            <li className="mb-2">Control app permissions in device settings</li>
            <li className="mb-2">Limit ad personalization via device settings</li>
            <li className="mb-2">Request access/correction/deletion</li>
          </ul>

          <h3 className={`text-xl font-bold mt-6 mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>9) Your rights & how to make requests</h3>
          <p className={`mb-4 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            You may request to access, correct, or delete your personal data. Email us at <a href="mailto:Admin@smartreadgo.com" className="text-blue-600 hover:underline">Admin@smartreadgo.com</a> with the subject line: "Privacy Request – SmartReadGo".
          </p>

          <h3 className={`text-xl font-bold mt-6 mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>Contact</h3>
          <p className={`mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>SmartReadGo (Proprietorship)</p>
          <p className={`mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Mumbai, Maharashtra, India</p>
          <p className={`mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Email: <a href="mailto:Admin@smartreadgo.com" className="text-blue-600 hover:underline">Admin@smartreadgo.com</a></p>
          <p className={`mb-6 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>WhatsApp: <a href="https://wa.me/919004685736" className="text-blue-600 hover:underline">+91 9004685736</a></p>

          <div className="h-20" />
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
